import { useNavigate } from 'react-router-dom'

import { Alert, Box, Button, Href, Text } from '@factorialco/deprecated-design-system'
import { ImportCandidatesDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'

interface ImportCandidatesForm {
  file: File | null
}

const ImportCandidatesPage = () => {
  const ACCEPTED_CONTENT_TYPES = acceptedContentTypes([
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ])

  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { access } = useCurrentSession()
  const [importCandidates] = useMutation(ImportCandidatesDocument, {
    extractorFn: (data) => data.atsMutations.createAtsCandidateImport,
  })
  const navigateToCandidates = () => navigate(navigation.ats.candidates.fullPath())
  const importTemplateUrl = buildUrl('/ats/candidates/imports/new')

  const form = useForm<ImportCandidatesForm>({
    defaultValues: {
      file: null,
    },
    validate(values) {
      const errors: FormErrors<ImportCandidatesForm> = {}

      if (!values.file) {
        errors.file = i18n.t('common.validations.empty')
      }

      return errors
    },
    async onSubmit(values, { setErrors }) {
      const result = await importCandidates({
        variables: {
          accessId: access.id,
          file: values.file,
        },
      })

      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        navigateToCandidates()
      }

      return handled
    },
  })

  const onDropFile = (files: File[]) => {
    form.setFieldValue('file', files[0])
  }

  return (
    <Modal
      illustration="people-add"
      title={i18n.t('ats.candidates.import_candidates.title')}
      description={i18n.t('ats.candidates.import_candidates.description')}
      onClose={navigateToCandidates}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Button submit disabled={form.isSubmitting}>
          {i18n.t('ats.candidates.import_candidates.submit')}
        </Button>
      }
    >
      <Box gap="s24">
        <Href href={importTemplateUrl} download>
          {i18n.t('ats.candidates.import_candidates.download_template')}
        </Href>
        <Box gap="s8">
          <Text.Normal size="200" color="grey800" weight="medium">
            {i18n.t('ats.candidates.import_candidates.upload_file')}
          </Text.Normal>
          <DropzoneBox
            iconName="common-file-text-upload"
            message={
              form.values.file?.name || i18n.t('ats.candidates.import_candidates.choose_file')
            }
            onDrop={onDropFile}
            multiple={false}
            accept={ACCEPTED_CONTENT_TYPES}
          />
          {form.errors.file && <Text.Normal color="radicalRed800">{form.errors.file}</Text.Normal>}
        </Box>

        {form.values.file && (
          <Alert>{i18n.t('ats.candidates.import_candidates.data_protection_warning')}</Alert>
        )}
      </Box>
    </Modal>
  )
}

export default ImportCandidatesPage
